import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { crmPostventaIpcApi } from "../crmPostventaIpcApi";
import Alert from "../../crmpostventa-shared/components/Alert";
import Modal from "../../crmpostventa-shared/components/Modal";
import { labelFor, todayIso } from "../../crmpostventa-shared/crmFormat";
import { ESTADO_LABELS, isOpenGestion, isProactiva } from "../ipcFormat";
import AsignarGestionDialog from "./AsignarGestionDialog";
import useOwnershipCommands from "./useOwnershipCommands";
import { ActionCards, ActionForm, FinalizarForm, GestionHeader, LinksBand, Timeline } from "./GestionarIpcParts";
import {
  ACCIONES,
  CONFLICT_TITLE,
  EMPTY_FINALIZAR,
  EMPTY_FORM,
  FINALIZAR_FIELD_IDS,
  accionCopy,
  actionsFor,
  buildAccionPayload,
  buildFinalizarPayload,
  createCorrelationId,
  describeSaveError,
  fieldId,
  finalizarFieldId,
  firstErrorKey,
  formForAccion,
  freshEntryIds,
  isConflict,
  isOpenEstado,
  ownershipFor,
  tipoMotivo,
  validateAccion,
  validateFinalizar,
} from "./gestionarIpcModel";

const TITLE_ID = "crm-ipc-gestionar-title";
const FINALIZAR_TITLE_ID = "crm-ipc-finalizar-title";
const CLOSED_FINALIZAR = { open: false, form: EMPTY_FINALIZAR, errors: {}, error: null, focus: 0 };

const withoutKey = (errors, key) => {
  if (!errors[key]) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
};

/**
 * "Gestionar" of a gestión IPC or Proactiva (design Prompts 4, 6 and 7; T5). Loads GET gestiones/{id} and shows the
 * header with the responsible actions (D25), the links band (Ver ficha and the motive buttons of N8), the actions of
 * the current state as RadioCards with their form, "Finalizar gestión…" in a small modal on top, and the timeline.
 *
 * Every command sends the current versionToken and one correlationId per attempt: the same data reuses it, so
 * "Reintentar" after a network error gets the server's idempotent answer (RN5). Actions and the responsible commands
 * (Tomar, Liberar) keep separate attempts and errors, so each "Reintentar" repeats its own request. After an action
 * that does not close the gestión the modal stays open, reloads the detail and highlights the new timeline entry
 * (Prompt 6). A version conflict (409) shows the conflict alert; "Recargar" reloads and keeps what the asesor typed
 * (the action form and the Finalizar input), dropping only an action the new state no longer offers. Any other 409
 * shows the server message. Without manage permission it is read-only; a closed gestión points to the Detalle
 * (onVerDetalle).
 *
 * onUpdated({ title, text, closed }) runs after each successful command (the caller shows the toast and refreshes the
 * queue; closed means Finalizar, and the caller closes the modal).
 */
export default function GestionarIpcModal({
  gestionId,
  row = null,
  responsables = [],
  currentUserId = null,
  darkMode = false,
  onClose,
  onUpdated,
  onVerDetalle,
  onNavigate,
}) {
  const [detail, setDetail] = useState({ status: "loading", data: null, error: null });
  const [accion, setAccion] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [freshIds, setFreshIds] = useState([]);
  const [focusRequest, setFocusRequest] = useState(0);
  const [finalizar, setFinalizar] = useState(CLOSED_FINALIZAR);
  const [assignOpen, setAssignOpen] = useState(false);
  // Attempt (body key and correlationId) of the last action; Tomar / Liberar keep theirs in useOwnershipCommands.
  const attemptRef = useRef(null);
  const mountedRef = useRef(true);
  // Timeline of the last load, to tell which entries a reload adds.
  const timelineRef = useRef([]);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const data = detail.data;
  const gestion = data?.gestion || row || null;
  const versionToken = data?.versionToken || data?.gestion?.versionToken || null;
  const open = isOpenGestion(gestion) && isOpenEstado(gestion?.estado);
  const loaded = detail.status === "done";
  const canManage = loaded && open && data?.puedeGestionar !== false;
  const comercio = gestion?.nombreComercio || "";
  const ownership = ownershipFor(data, gestion, canManage);

  // A reload (after a command or "Recargar") keeps the form and marks the new timeline entries. Answers the loaded
  // detail, or null when it failed.
  const load = useCallback(
    async (reload = false) => {
      if (!reload) setDetail({ status: "loading", data: null, error: null });
      try {
        const response = await crmPostventaIpcApi.obtenerGestion(gestionId);
        if (!mountedRef.current) return null;
        setFreshIds(reload ? freshEntryIds(timelineRef.current, response?.timeline) : []);
        timelineRef.current = response?.timeline || [];
        setDetail({ status: "done", data: response || {}, error: null });
        return response || {};
      } catch (loadError) {
        if (!mountedRef.current) return null;
        if (!reload) setDetail({ status: "error", data: null, error: loadError });
        return null;
      }
    },
    [gestionId]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!focusRequest) return;
    const key = firstErrorKey(errors);
    if (key) document.getElementById(fieldId(key))?.focus();
    // Only a new submit moves the focus, not every error change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  useEffect(() => {
    if (!finalizar.open) return;
    const key = firstErrorKey(finalizar.errors, FINALIZAR_FIELD_IDS) || "resultado";
    document.getElementById(finalizarFieldId(key))?.focus();
    // Opening Finalizar and each failed confirm move the focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalizar.open, finalizar.focus]);

  const chooseAccion = (next) => {
    if (next === accion) return;
    setAccion(next);
    setForm(formForAccion(next, gestion));
    setErrors({});
    setSubmitError(null);
  };

  const changeField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => withoutKey(current, key));
  };

  const showConflict = (error) => {
    setConflict({ message: error?.message || "" });
    setFinalizar((current) => ({ ...current, open: false }));
    setAssignOpen(false);
  };

  // The same body (same data and versionToken) reuses the correlationId of its attempt.
  const correlationFor = (body) => {
    const key = JSON.stringify(body);
    if (attemptRef.current?.key !== key) attemptRef.current = { key, correlationId: createCorrelationId() };
    return attemptRef.current.correlationId;
  };

  // A command that keeps the gestión open: reload, highlight the new entry and tell the caller (toast, queue). The
  // versionToken changes, so no attempt can be repeated (the responsible commands key theirs by versionToken too).
  const afterSuccess = async (title, text = comercio) => {
    attemptRef.current = null;
    onUpdated?.({ title, text, closed: false });
    await load(true);
  };

  // ---- Responsible (D25): Tomar, Liberar and Asignar… keep their own attempt and error.
  const owner = useOwnershipCommands({
    gestionId,
    versionToken,
    comercio,
    saving,
    setSaving,
    mountedRef,
    onStart: () => setSubmitError(null),
    onSuccess: (title, text) => {
      setAssignOpen(false);
      return afterSuccess(title, text);
    },
    onConflict: showConflict,
  });

  const send = async (payload, fromFinalizar = false) => {
    const body = { ...payload, versionToken };
    const correlationId = correlationFor(body);
    setSaving(true);
    setSubmitError(null);
    owner.clearError();
    setFinalizar((current) => ({ ...current, error: null }));
    try {
      await crmPostventaIpcApi.ejecutarAccion(gestionId, { ...body, correlationId });
      if (!mountedRef.current) return;
      const copy = accionCopy(payload.accion, gestion?.estado);
      if (payload.accion === ACCIONES.FINALIZAR) {
        onUpdated?.({ title: copy.toast, text: comercio, closed: true });
        return;
      }
      setAccion(null);
      setForm(EMPTY_FORM);
      setErrors({});
      await afterSuccess(copy.toast);
      if (mountedRef.current) setSaving(false);
    } catch (actionError) {
      if (!mountedRef.current) return;
      setSaving(false);
      if (isConflict(actionError)) {
        showConflict(actionError);
        return;
      }
      const described = describeSaveError(actionError);
      if (fromFinalizar) setFinalizar((current) => ({ ...current, error: described }));
      else setSubmitError(described);
    }
  };

  const submit = () => {
    if (saving || !accion || !canManage) return;
    const nextErrors = validateAccion(accion, form, { gestion, now: new Date() });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setFocusRequest((count) => count + 1);
      return;
    }
    send(buildAccionPayload(accion, form));
  };

  const reload = async () => {
    setReloading(true);
    setSubmitError(null);
    owner.clearError();
    const response = await load(true);
    if (!mountedRef.current) return;
    setReloading(false);
    if (response) {
      setConflict(null);
      // The typed input stays, unless the new state no longer offers the chosen action (it would fail again).
      if (accion && !actionsFor(response.gestion?.estado).includes(accion)) {
        setAccion(null);
        setForm(EMPTY_FORM);
        setErrors({});
      }
    } else setSubmitError({ title: "No pudimos recargar la gestión.", text: "Probá de nuevo en unos segundos.", retry: false });
  };

  // ---- Finalizar.
  // Reopening keeps what was typed (after "Volver" or a conflict and "Recargar").
  const openFinalizar = () => setFinalizar((current) => ({ ...current, open: true, errors: {}, error: null }));
  const closeFinalizar = () => setFinalizar((current) => ({ ...current, open: false }));

  const changeFinalizarField = (key, value) =>
    setFinalizar((current) => {
      const nextForm = { ...current.form, [key]: value, ...(key === "resultado" ? { motivoCierre: "" } : {}) };
      let nextErrors = withoutKey(current.errors, key);
      if (key === "resultado") nextErrors = withoutKey(withoutKey(nextErrors, "motivoCierre"), "observacion");
      return { ...current, form: nextForm, errors: nextErrors };
    });

  const confirmFinalizar = () => {
    if (saving) return;
    const nextErrors = validateFinalizar(finalizar.form);
    if (Object.keys(nextErrors).length) {
      setFinalizar((current) => ({ ...current, errors: nextErrors, focus: current.focus + 1 }));
      return;
    }
    send(buildFinalizarPayload(finalizar.form), true);
  };

  const renderMain = () => {
    if (detail.status === "loading") {
      return (
        <div className="crm-rollo__context-skel" aria-busy="true" aria-label="Cargando la gestión">
          {["60%", "85%", "45%", "70%"].map((width) => (
            <span key={width} className="crm-rollo__skel crm-rollo__skel--tall" style={{ width }} aria-hidden="true" />
          ))}
        </div>
      );
    }

    if (detail.status === "error") {
      return (
        <div className="crm-rollo__note crm-rollo__note--error" role="alert">
          <span className="crm-rollo__note-ico" aria-hidden="true">
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <b>No pudimos cargar la gestión</b>
          <p>{detail.error?.message || "Probá de nuevo en unos segundos."}</p>
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={() => load()}>
            <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      );
    }

    return (
      <>
        {!open ? (
          <Alert tone="info" role="status" title={`Esta gestión está finalizada (${labelFor(ESTADO_LABELS, gestion?.estado)}).`}>
            Se consulta desde el Detalle.
          </Alert>
        ) : null}
        {open && !canManage ? (
          <Alert tone="info" role="status" title="No tenés permiso para gestionar esta gestión.">
            Podés ver su estado y su historial, pero no avanzarla.
          </Alert>
        ) : null}
        {conflict ? (
          <div className="crm-rollo__conflict">
            <Alert tone="error" role="alert" title={CONFLICT_TITLE}>
              Recargá para ver los cambios. Lo que cargaste se mantiene.
            </Alert>
            <div>
              <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={reloading} onClick={reload}>
                <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                Recargar
              </button>
            </div>
          </div>
        ) : null}
        {owner.error ? (
          <div className="crm-rollo__conflict">
            <Alert tone="error" role="alert" title={owner.error.title}>
              {owner.error.text}
            </Alert>
            {owner.error.retry && canManage ? (
              <div>
                <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={saving} onClick={owner.retry}>
                  <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                  Reintentar
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        {submitError ? (
          <div className="crm-rollo__conflict">
            <Alert tone="error" role="alert" title={submitError.title}>
              {submitError.text}
            </Alert>
            {submitError.retry && accion ? (
              <div>
                <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" disabled={saving} onClick={submit}>
                  <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
                  Reintentar
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
        {canManage ? (
          <ActionCards
            actions={actionsFor(gestion?.estado)}
            value={accion}
            estado={gestion?.estado}
            disabled={saving}
            onChange={chooseAccion}
            onFinalizar={openFinalizar}
          />
        ) : null}
        {canManage && accion ? (
          <ActionForm accion={accion} gestion={gestion} form={form} errors={errors} saving={saving} now={new Date()} onChange={changeField} />
        ) : null}
      </>
    );
  };

  const closeButton = (
    <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={onClose}>
      Cerrar
    </button>
  );

  let footer;
  if (canManage) {
    footer = (
      <>
        <span className="crm-rollo__grow crm-rollo__help">
          {accion ? (
            <>
              <span className="crm-rollo__req">*</span> Obligatorio
            </>
          ) : (
            "Elegí una acción para continuar"
          )}
        </span>
        {closeButton}
        <button
          type="button"
          className="crm-rollo__btn crm-rollo__btn--primary"
          disabled={!accion || saving}
          aria-busy={saving || undefined}
          onClick={submit}
        >
          {saving ? (
            <>
              <span className="crm-rollo__spinner" aria-hidden="true" />
              Guardando…
            </>
          ) : accion ? (
            accionCopy(accion, gestion?.estado).btn
          ) : (
            "Guardar"
          )}
        </button>
      </>
    );
  } else {
    footer = (
      <>
        <span className="crm-rollo__grow" />
        {loaded && !open && onVerDetalle ? (
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={() => onVerDetalle(gestion)}>
            Ver detalle
          </button>
        ) : null}
        {closeButton}
      </>
    );
  }

  const headExtra = gestion ? (
    <>
      <GestionHeader
        gestion={gestion}
        today={todayIso()}
        currentUserId={currentUserId}
        ownership={ownership}
        busy={saving}
        onTomar={owner.tomar}
        onLiberar={owner.liberar}
        onAsignar={() => setAssignOpen(true)}
      />
      {loaded ? <LinksBand gestion={gestion} editable={canManage} onNavigate={onNavigate} /> : null}
    </>
  ) : null;

  return (
    <>
      <Modal
        titleId={TITLE_ID}
        title={comercio || "Gestión"}
        stepLabel={`Gestionar · ${isProactiva(gestion) ? "Proactiva" : "Gestión IPC"}`}
        size="lg"
        className="crm-ipc"
        darkMode={darkMode}
        closeDisabled={saving || finalizar.open || assignOpen}
        onClose={onClose}
        headExtra={headExtra}
        bodyClassName="crm-rollo__manage"
        footer={footer}
      >
        <div className="crm-rollo__manage-main">{renderMain()}</div>
        <aside className="crm-rollo__manage-side" aria-label="Línea de tiempo">
          {loaded ? <Timeline entries={data?.timeline} gestion={gestion} freshIds={freshIds} /> : null}
        </aside>
      </Modal>

      {finalizar.open ? (
        <Modal
          titleId={FINALIZAR_TITLE_ID}
          title="Finalizar gestión"
          size="xs"
          className="crm-ipc"
          darkMode={darkMode}
          closeDisabled={saving}
          onClose={closeFinalizar}
          headExtra={<span className="crm-rollo__s">{`${comercio} · ${tipoMotivo(gestion)}`}</span>}
          footer={
            <>
              <span className="crm-rollo__grow" />
              <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" disabled={saving} onClick={closeFinalizar}>
                Volver
              </button>
              <button
                type="button"
                className="crm-rollo__btn crm-rollo__btn--primary"
                disabled={saving}
                aria-busy={saving || undefined}
                onClick={confirmFinalizar}
              >
                {saving ? (
                  <>
                    <span className="crm-rollo__spinner" aria-hidden="true" />
                    Guardando…
                  </>
                ) : (
                  "Finalizar gestión"
                )}
              </button>
            </>
          }
        >
          <FinalizarForm
            form={finalizar.form}
            errors={finalizar.errors}
            saving={saving}
            submitError={finalizar.error}
            onChange={changeFinalizarField}
          />
        </Modal>
      ) : null}

      {assignOpen && gestion ? (
        <AsignarGestionDialog
          gestion={gestion}
          responsables={responsables}
          currentUserId={currentUserId}
          darkMode={darkMode}
          onClose={() => setAssignOpen(false)}
          onSubmit={owner.asignar}
        />
      ) : null}
    </>
  );
}
